import API_CONFIG from './config';

class HttpClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.defaultHeaders = API_CONFIG.HEADERS;
    try {
      // eslint-disable-next-line no-console
      console.log('httpClient: constructed with baseURL =', this.baseURL || '(empty)');
    } catch {}
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('auth_token');
  }

  // Set auth token to localStorage
  setAuthToken(token) {
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  // Get headers with auth token if available
  getHeaders() {
    const headers = { ...this.defaultHeaders };
    const token = this.getAuthToken();
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Build full URL, supporting absolute endpoints
  buildUrl(endpoint) {
    if (!endpoint) return this.baseURL || '';
    // If endpoint already absolute, return as is
    if (/^https?:\/\//i.test(endpoint)) return endpoint;
    // Read BASE_URL at call time and fall back to runtime env injection
    const runtimeBase = (API_CONFIG.BASE_URL || ((typeof window !== 'undefined' && window._env_ && window._env_.REACT_APP_BACKEND_URL) ? window._env_.REACT_APP_BACKEND_URL : '') || '').replace(/\/$/, '');
    const base = runtimeBase;
    const path = String(endpoint).startsWith('/') ? endpoint : `/${endpoint}`;
    const full = `${base}${path}`;
    try {
      // eslint-disable-next-line no-console
      console.log('httpClient.buildUrl:', { endpoint, base, full });
    } catch {}
    return full;
  }

  // Make HTTP request
  async request(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    // If options.headers is provided, merge them with default headers
    // But be careful not to override FormData headers
    if (options.headers) {
      config.headers = { ...this.getHeaders(), ...options.headers };
      
      // If this is a FormData request, ensure Content-Type is not set
      if (options.body instanceof FormData) {
        delete config.headers['Content-Type'];
      }
    }

    // Ensure body is included in the config
    if (options.body) {
      config.body = options.body;
    }

    try {
      const response = await fetch(url, config);
      
      // Handle different response statuses
      if (response.ok) {
        const data = await response.json();
        console.log('HttpClient: Parsed response data:', data);
        return { success: true, data, status: response.status };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('HttpClient: Error response data:', errorData);
        
        // Handle 403: never redirect, always return not-allowed message
        if (response.status === 403) {
          return {
            success: false,
            data: errorData,
            status: 403,
            message: errorData.message || 'غير مسموح'
          };
        }

        // Handle 401: allow suppression; otherwise redirect to login
        if (response.status === 401 || errorData.message === 'Unauthenticated.') {
          if (options && options.suppressAuthRedirect) {
            return {
              success: false,
              data: errorData,
              status: response.status,
              message: errorData.message || 'Unauthenticated'
            };
          }

          // Clear auth data and redirect to login by default
          localStorage.removeItem('auth_token');
          localStorage.removeItem('admin_name');
          localStorage.removeItem('user_data');
          window.location.href = '#/auth/signin';
          return {
            success: false,
            data: errorData,
            status: response.status,
            message: 'Unauthenticated. Redirecting to login...'
          };
        }
        
        return {
          success: false,
          data: errorData,
          status: response.status,
          message: errorData.message || `HTTP ${response.status} error`
        };
      }
    } catch (error) {
      console.error('HttpClient: Request error:', error);
      return {
        success: false,
        data: null,
        status: 0,
        message: error.message || 'Network error occurred'
      };
    }
  }

  // GET request
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST request
  async post(endpoint, data) {
    let body = data;
    const options = {
      method: 'POST'
    };

    // If data is FormData, don't set Content-Type header
    if (data instanceof FormData) {
      const headers = { ...this.getHeaders() };
      delete headers['Content-Type']; // Let browser set the boundary
      options.headers = headers;
    } else {
      // For JSON data, stringify it and set Content-Type
      body = JSON.stringify(data);
      options.headers = {
        ...this.getHeaders(),
        'Content-Type': 'application/json'
      };
    }

    options.body = body;
    return this.request(endpoint, options);
  }

  // PUT request
  async put(endpoint, data) {
    let body = data;
    const options = {
      method: 'PUT'
    };

    // If data is FormData, don't set Content-Type header
    if (data instanceof FormData) {
      const headers = { ...this.getHeaders() };
      delete headers['Content-Type']; // Let browser set the boundary
      options.headers = headers;
    } else {
      // For JSON data, stringify it and set Content-Type
      body = JSON.stringify(data);
      options.headers = {
        ...this.getHeaders(),
        'Content-Type': 'application/json'
      };
    }

    options.body = body;
    return this.request(endpoint, options);
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

export default new HttpClient();
